import * as React from "react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateDocument } from "@/hooks/useCreateDocument";
import { useDocument, WORD_DOCUMENT_INFO_QUERY_KEY } from "@/hooks/useDocument";
import { useUser } from "@/hooks/useUser";
import { rebindWordDocumentId } from "@/utils/document.utils";
import {
  buildCreateDocumentPayload,
  uploadCreatedDocumentText,
} from "@/hooks/document-creation-upload.utils";
import { toast } from "sonner";
import { TEXT_UPLOAD_TIMEOUT_MS, withTimeout } from "@/utils/timeout";
import type { ICreateDocumentResponse } from "@/services/document.service";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Tell the user when we could not get any usable text out of their document.
 *
 * `null` means Word refused to give us the text at all — still syncing from a
 * document management system, password protected, or an image-only scan. The
 * upload is skipped elsewhere so we never overwrite good stored content with
 * blank, but until now nothing said anything: every review afterwards ran
 * against no text and quietly came back with nothing (QS-342).
 */
const warnIfDocumentUnusable = (plainText: string | null) => {
  if (plainText === null) {
    toast.error("Couldn't read this document", {
      description:
        "Quillio can't read the text of this file, so reviews and requests will have nothing to work with. If it's still syncing from your document system, is password protected, or is a scan, open it directly, let it finish loading, then reopen Quillio.",
    });
    return;
  }

  if (plainText.trim() === "") {
    toast.warning("This document looks empty", {
      description:
        "Quillio didn't find any text in this file. Reviews and requests won't have anything to work with until it has content.",
    });
  }
};

async function afterDocumentCreated(
  data: ICreateDocumentResponse,
  plainText: string | null,
  queryClient: QueryClient
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["document"] });
  if (data.document_id == null || plainText == null) return;
  try {
    await withTimeout(
      uploadCreatedDocumentText(data.document_id, plainText),
      TEXT_UPLOAD_TIMEOUT_MS,
      "uploadCreatedDocumentText timed out"
    );
  } catch (error) {
    console.error("Document text upload failed or timed out", error);
  }
}

function axiosStatus(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined;
  return error.response?.status;
}

export const useDocumentCreationOnError = () => {
  const { user } = useUser();
  const { data: document, error: documentError } = useDocument();
  const { mutate: createDocument, isPending: isCreating } = useCreateDocument();
  const queryClient = useQueryClient();
  const documentCreationAttemptedRef = React.useRef<boolean>(false);
  const rebindAttemptedRef = React.useRef<boolean>(false);
  const rebindInFlightRef = React.useRef<boolean>(false);
  const [showRequestAccess, setShowRequestAccess] = React.useState(false);

  React.useEffect(() => {
    if (document != null) {
      rebindAttemptedRef.current = false;
      setShowRequestAccess(false);
    }
    if (document != null || documentError == null) {
      documentCreationAttemptedRef.current = false;
    }
  }, [document, documentError]);

  React.useEffect(() => {
    if (document != null || axiosStatus(documentError) !== 403) return;
    if (rebindInFlightRef.current) return;
    if (rebindAttemptedRef.current) {
      setShowRequestAccess(true);
      return;
    }
    rebindAttemptedRef.current = true;
    rebindInFlightRef.current = true;
    void (async () => {
      try {
        await rebindWordDocumentId();
        await queryClient.invalidateQueries({ queryKey: WORD_DOCUMENT_INFO_QUERY_KEY });
        queryClient.removeQueries({ queryKey: ["document"] });
      } catch (error) {
        console.error("Failed to rebind Word document id:", error);
        setShowRequestAccess(true);
      } finally {
        rebindInFlightRef.current = false;
      }
    })();
  }, [document, documentError, queryClient]);

  React.useEffect(() => {
    const shouldCreate =
      axiosStatus(documentError) === 404 &&
      user != null &&
      !isCreating &&
      !documentCreationAttemptedRef.current;

    if (!shouldCreate || user == null) {
      return;
    }

    documentCreationAttemptedRef.current = true;
    const userId = user.id;
    const resetAttempt = () => {
      documentCreationAttemptedRef.current = false;
    };

    void (async () => {
      try {
        const { payload, plainText } = await buildCreateDocumentPayload(userId);
        warnIfDocumentUnusable(plainText);
        createDocument(payload, {
          onSuccess: (data) => afterDocumentCreated(data, plainText, queryClient),
          onError: resetAttempt,
        });
      } catch (error) {
        console.error("Failed to create document:", error);
        resetAttempt();
      }
    })();
  }, [documentError, user, createDocument, isCreating, queryClient]);

  return { showRequestAccess };
};
